export interface NavigationIntent {
  url: URL;
  source: "initial" | "push" | "pop";
  restoreScrollY?: number;
}

interface HistoryEntryState {
  key: string;
  scrollY: number;
}

type NavigateHandler = (intent: NavigationIntent) => Promise<void>;

function isHistoryEntryState(value: unknown): value is HistoryEntryState {
  if (!value || typeof value !== "object") return false;
  const state = value as Partial<HistoryEntryState>;
  return typeof state.key === "string" && typeof state.scrollY === "number";
}

function createHistoryKey(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export class Router {
  readonly #onNavigate: NavigateHandler;
  readonly #scrollPositions = new Map<string, number>();
  readonly #handleClick = (event: MouseEvent): void => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) return;

    const anchor = target.closest<HTMLAnchorElement>("a[data-router-link]");
    if (!anchor || anchor.target === "_blank" || anchor.hasAttribute("download")) return;

    const url = new URL(anchor.href, window.location.href);
    if (url.origin !== window.location.origin) return;

    event.preventDefault();
    void this.navigate(url);
  };

  readonly #handlePopState = (event: PopStateEvent): void => {
    const state = isHistoryEntryState(event.state) ? event.state : undefined;
    void this.#onNavigate({
      url: new URL(window.location.href),
      source: "pop",
      restoreScrollY: state ? (this.#scrollPositions.get(state.key) ?? state.scrollY) : 0,
    });
  };

  readonly #handleScroll = (): void => {
    if (!isHistoryEntryState(history.state)) return;
    this.#scrollPositions.set(history.state.key, window.scrollY);
  };

  constructor(onNavigate: NavigateHandler) {
    this.#onNavigate = onNavigate;
  }

  async start(): Promise<void> {
    history.scrollRestoration = "manual";
    const state = isHistoryEntryState(history.state) ? history.state : { key: createHistoryKey(), scrollY: window.scrollY };
    history.replaceState(state, "", window.location.href);
    this.#scrollPositions.set(state.key, state.scrollY);
    document.addEventListener("click", this.#handleClick);
    window.addEventListener("popstate", this.#handlePopState);
    window.addEventListener("scroll", this.#handleScroll, { passive: true });

    await this.#onNavigate({ url: new URL(window.location.href), source: "initial" });
  }

  async navigate(destination: string | URL): Promise<void> {
    const url = destination instanceof URL ? destination : new URL(destination, window.location.href);
    if (url.origin !== window.location.origin) {
      window.location.assign(url.href);
      return;
    }

    this.#saveCurrentScroll();
    const nextState = { key: createHistoryKey(), scrollY: 0 } satisfies HistoryEntryState;
    history.pushState(nextState, "", url);
    this.#scrollPositions.set(nextState.key, 0);
    await this.#onNavigate({ url, source: "push" });
  }

  destroy(): void {
    this.#saveCurrentScroll();
    document.removeEventListener("click", this.#handleClick);
    window.removeEventListener("popstate", this.#handlePopState);
    window.removeEventListener("scroll", this.#handleScroll);
    // Deliberately left on "manual": destroy() runs on pagehide, and handing scroll
    // restoration back to the browser there is what makes a reload keep a stale offset.
  }

  #saveCurrentScroll(): void {
    const previous = isHistoryEntryState(history.state) ? history.state : { key: createHistoryKey(), scrollY: 0 };
    const scrollY = window.scrollY;
    this.#scrollPositions.set(previous.key, scrollY);
    history.replaceState({ ...previous, scrollY } satisfies HistoryEntryState, "", window.location.href);
  }
}
